<template>
  <aside-modal 
    v-slot="{ close }" 
    v-model="bcStorageExplorerStore.show"
  >
    <div>
      <div class="bg-bc-blue text-white px-6 py-5 rounded-tl-md">
        <div class="flex items-center mb-2">
          <div class="text-lg flex-1">
            {{ $t('Upload from data storage or cloud') }}
          </div>
          <div>
            <aw-icon 
              icon="close" 
              class="w-6 h-6 current-fill text-white cursor-pointer -mr-1.5 -mt-2" 
              @click="closeModal(close)" 
            />
          </div>
        </div>
      </div>
    </div>
    <!-- CONTENT NOT STICKY, OVERFLOWED IF NEEDED -->
    <div
      class="p-6 overflow-y-auto flex-1 relative flex flex-col"
      :class="{'DataUpload__DataStorage': type === 'dataStorage'}"
    >
      <div class="flex flex-wrap items-center py-3">
        <nav 
          class="-mb-px flex flex-1" 
          aria-label="Tabs"
        >
          <tab-panel-pill
            v-for="(item, index) in tabs"
            :key="index"
            @click.stop.prevent="handleTypeChange(item.action)"
            :active="item.action === type"
            :icon="item.icon"
            :text="$t(item.text)"
            class="pill__Tab-white pill__Tab-hiddenTextMobile"
          />
        </nav>
        <button 
          v-if="state === 'data' && ['onedrive', 'googledrive'].includes(type)"
          class="flex items-center text-sm cursor-pointer mr-2"
          @click.prevent.stop="startLogout()"
        >
          <aw-icon
            icon="logout"
            class="w-4 h-4"
          />
        </button>
        <button 
          class="flex items-center text-sm cursor-pointer"
          @click.prevent.stop="toggleView()"
        >
          <aw-icon
            :icon="view === 'grid' ? 'list' : 'grid'"
            class="w-4 h-4"
          />
        </button>
      </div>
      <template v-if="bcStorageExplorerStore.show && state === 'data'">
        <storage-explorer-data-storage
          v-if="type === 'dataStorage'"
          :view="view"
          :selected-items="selectedItems"
        />
        <storage-explorer-data-cloud
          v-else-if="type === 'onedrive'"
          :view="view"
          :selected-items="selectedItems"
          type="onedrive"
          @unauthenticated="unauthenticate"
        />
        <storage-explorer-data-cloud
          v-else-if="type === 'googledrive'"
          :view="view"
          :selected-items="selectedItems"
          type="googledrive"
          @unauthenticated="unauthenticate"
        />
      </template>
      <storage-explorer-authentication
        v-else-if="bcStorageExplorerStore.show && state === 'authentication'"
        :authenticate="drives[type]?.authenticate"
        :type="type"
      />
    </div>
    <!-- STICKY FOOTER - SUBMIT BUTTONS ALMOST -->
    <div class="flex-shrink-0 p-5 border-t border-gray-400">
      <div class="flex justify-end">
        <button 
          type="button" 
          class="btn btn-white" 
          @click="closeModal(close)"
        >
          <aw-icon
            icon="close"
            class="w-5 h-5 "
          />
          {{ $t('Cancel') }}
        </button>

        <button
          type="button"
          class="ml-3 btn"
          :class="{
            'pointer-events-none': saveLoading || !numOfSelectedItems,
            'btn-primary': numOfSelectedItems,
            'text-gray-300': !numOfSelectedItems
          }"
          :disabled="!numOfSelectedItems"
          @click="save(close)"
        >
          <aw-icon
            :icon="saveLoading ? 'loading' : 'save-floppy-disc'"
            class="w-5 h-5 mr-1"
            :class="{ 'animate-spin': saveLoading }"
          />
          {{ $t('Use files ') }}
          <span 
            v-if="numOfSelectedItems"
            style="min-width: 26px;"
          >
            &nbsp;({{ numOfSelectedItems }})
          </span>
          <span 
            v-else
            style="min-width: 26px;"
          />
        </button>
      </div>
    </div>
  </aside-modal>
</template>

<script>
import { mapStores } from 'pinia'
import useBcFileStore from '@/frontapp/store/bcFile'
import useBcStorageExplorerStore from '@/frontapp/store/bcStorageExplorer'
import { DriveApi, FileApi } from '@/frontapp/test-api'
import handleApiError from '../../utils/handleApiError'

const driveApi = new DriveApi({}, '/api/v2')
const fileApi = new FileApi({}, '/api/v2')

export default {
  props: {
    cloudEnabled: Boolean,
    drives: Object,
  },
  data() {
    return {
      type: 'dataStorage',
      view: 'list',
      isGoogleDriveLogged: false,
      selectedItems: new Map(),
      state: 'data',
      saveLoading: false,
    }
  },
  computed: {
    ...mapStores(useBcFileStore, useBcStorageExplorerStore),
    numOfSelectedItems() {
      const selectedItemsKeys = Array.from(this.selectedItems.keys())
      return selectedItemsKeys.length
    },
    tabs() {
      const tabs = [{
        action: 'dataStorage',
        icon: 'storage',
        text: 'Data storage'
      }]

      if (this.cloudEnabled) {
        tabs.push({
          action: 'onedrive',
          icon: 'one-drive',
          text: 'One Drive'
        })
      
        tabs.push({
          action: 'googledrive',
          icon: 'add-to-google-drive',
          text: 'Google Drive'
        })
      }

      return tabs
    }
  },
  methods: {
    startLogout() {
      window.open(this.drives[this.type].logout, '_blank')
    },
    handleAuthChange(event) {
      if (event.key === 'oauthState') {
        const stateData = JSON.parse(event.newValue)
        const drives = JSON.parse(JSON.stringify(this.drives))
        if (stateData.state === 'success') {
          drives[stateData.ident].authenticated = true
          this.$store.commit('nette/updateControl', {
            controlName: 'storageExplorer',
            data: drives,
            key: 'drives'
          })

          this.state = 'data'
        } else if (stateData.state === 'logout') {
          this.unauthenticate(stateData.ident)
        }
      }
    },
    unauthenticate(type) {
      const drives = JSON.parse(JSON.stringify(this.drives))
      drives[type].authenticated = false

      this.$store.commit('nette/updateControl', {
        controlName: 'storageExplorer',
        data: drives,
        key: 'drives'
      })

      this.state = 'authentication'
    },
    toggleView() {
      if (this.view === 'grid') {
        this.view = 'list'
      } else {
        this.view = 'grid'
      }
    },
    closeModal (close) {
      this.type = 'dataStorage'
      this.view = 'list'
      this.bcFileStore.order = 'uploaded'
      this.bcFileStore.dir = 'desc'
      //this.isGoogleDriveLogged = false
      //this.oneDriveAuthenticated = false
      this.selectedItems.clear()
      window.removeEventListener("message", this.handleAuthValid)
      this.bcStorageExplorerStore.closeModal()

      close()
    },
    handleTypeChange(type) {
      if (type !== 'dataStorage') {
        if (!this.drives[type].authenticated) {
          this.state = 'authentication'

        } else {
          this.state = 'data'
        }
      }

      this.type = type
      this.selectedItems.clear()
    },
    async save(close) {
      this.saveLoading = true
      if (this.type === 'dataStorage') {
        this.selectedItems.forEach((item) => {
          this.bcStorageExplorerStore.fileUploadDone(item)
        })

        const selectedItemsArray = Array.from(this.selectedItems.values())
        this.bcStorageExplorerStore.allFilesUploadDone(selectedItemsArray)
      } else {
        const existingItemsArray = []
        
        const filesRequests = Array.from(this.selectedItems.values()).map(async (item) => {
          const result = await handleApiError(() => driveApi.driveRequestDownload({
            ident: this.type,
            path: item.id,
          }))

          const fileRequest = result.data

          if (!fileRequest.file_id) {
            this.bcStorageExplorerStore.remoteFileUploadRequest(result.data)
          } else {
            const fileDetailResponse = await handleApiError(() => fileApi.fileDetail(fileRequest.file_id))
            this.bcStorageExplorerStore.fileUploadDone(fileDetailResponse.data)
            existingItemsArray.push(fileDetailResponse.data)
          }
        })

        await Promise.all(filesRequests)

        if (existingItemsArray.length > 0) {
          this.bcStorageExplorerStore.allFilesUploadDone(existingItemsArray)
        }
      }
      this.saveLoading = false
      this.closeModal(close)
    },
  },
  watch: {
    'bcStorageExplorerStore.show': {
      handler(val) {
        if (val) {
          window.localStorage.removeItem('oauthState')
          window.addEventListener('storage', this.handleAuthChange)
        } else {
          window.removeEventListener('storage', this.handleAuthChange)
        }
      }
    },
  }
}
</script>

<style lang="scss" scoped>
</style>
